import apiClient from './client';

export type BillingPlan = 'monthly' | 'yearly';

export interface BillingDetails {
  legalName: string;
  email: string;
  phone: string;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  state: string;
  pincode: string;
  gstin?: string;
}

export interface PlanQuote {
  code: BillingPlan;
  label: string;
  amountPaise: number;
  gstPaise: number;
  totalPaise: number;
  periodMonths: number;
  currency: string;
}

export interface Invoice {
  _id: string;
  invoiceNumber: string;
  plan: BillingPlan;
  subtotalPaise: number;
  gstPaise: number;
  totalPaise: number;
  billing: BillingDetails;
  issuer: Record<string, any>;
  websiteName?: string;
  paidAt: string;
  createdAt: string;
}

export const formatInrFromPaise = (paise: number) =>
  `₹${(paise / 100).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const billingApi = {
  getCheckoutContext: async (websiteId: string) => {
    const response = await apiClient.get(`/business/billing/checkout-context/${websiteId}`);
    return response.data.data;
  },

  checkout: async (websiteId: string, plan: BillingPlan, billing: BillingDetails) => {
    const response = await apiClient.post('/business/billing/checkout', { websiteId, plan, billing });
    return response.data.data;
  },

  verify: async (payload: { websiteId: string; orderId: string; paymentId?: string; signature?: string }) => {
    const response = await apiClient.post('/business/billing/verify', payload);
    return response.data.data as { paymentId: string; invoiceId: string; invoiceNumber: string; published: boolean };
  },

  listInvoices: async (): Promise<Invoice[]> => {
    const response = await apiClient.get('/business/billing/invoices');
    return response.data.data;
  },

  getInvoice: async (invoiceId: string): Promise<Invoice> => {
    const response = await apiClient.get(`/business/billing/invoices/${invoiceId}`);
    return response.data.data;
  },

  getBillingProfile: async (): Promise<BillingDetails | null> => {
    const response = await apiClient.get('/business/billing/profile');
    return response.data.data;
  },

  saveBillingProfile: async (billing: BillingDetails): Promise<BillingDetails> => {
    const response = await apiClient.put('/business/billing/profile', billing);
    return response.data.data;
  },

  downloadInvoicePdf: async (invoiceId: string, invoiceNumber: string) => {
    const response = await apiClient.get(`/business/billing/invoices/${invoiceId}/pdf`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `${invoiceNumber}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  },

  getDomain: async (websiteId: string) => {
    const response = await apiClient.get(`/business/websites/${websiteId}/domain`);
    return response.data.data;
  },

  setDomain: async (websiteId: string, domain: string) => {
    const response = await apiClient.put(`/business/websites/${websiteId}/domain`, { domain });
    return response.data.data;
  },

  verifyDomain: async (websiteId: string) => {
    const response = await apiClient.post(`/business/websites/${websiteId}/domain/verify`);
    return response.data.data;
  },

  removeDomain: async (websiteId: string) => {
    const response = await apiClient.delete(`/business/websites/${websiteId}/domain`);
    return response.data.data;
  },
};
